import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../services/redux/store";
import {
  Typography,
  Grid,
  Box,
  Skeleton,
  useMediaQuery,
  useTheme,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
} from "@mui/material";
import DetailRow from "../common-features/DetailRow";
import { fetchUserApplicationsFormData } from "../../services/api/service/userApplicationService";
import UploadDocument from "./UploadDocument";
import LockSquare from "./../../assets/svgIcons/lock-square.svg";
import textFile from "./../../assets/images/icons/file-text.svg";
import Logo from "./../../assets/svgIcons/avanse-transparent-logo.png";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import eligibilityCheckServices from "../../services/api/service/eligibilityCheckService";
import { formatDateToShortMonth } from "../../utils/dateFormatUtils";
import CustomDivider from "../../components/common/CustomDivider";
import {
  setTrackingId,
  updateFormData,
  setFetchUserApplicationData,
  setDisplayHorizontalSteps,
  setApplicationSource,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import { TruncatedTextWithTooltip } from "../../components/ui/TruncatedTextWithTooltip";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTriggerData } from "../../hooks/useTriggerData";
import {
  getCombinedApplicationSteps,
} from "../../components/ui/StepperDyanmicForm/helper";
import {
  setBreData,
  setIsProvisionalOffer,
  setSummaryApplicationEditActive
} from "../../services/redux/slices/stepperDynamicFormSlice";
import {
  setApplicationStatus,
  setOpenFirstTimeOnlyPopup,
  setSelectedIndex
} from "../../services/redux/slices/tabsSlice";
import { setSalesUserId } from "../../services/redux/slices/chatSlice";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { setPartnerDetails, setCoApplicantDetails as setCoApplicantDetailsRedux } from "../../services/redux/slices/applicationLoanDetailsSlice";
import { APP_STATUS } from "../../utils/journeyConstants";
import { getApplicationMessage,getCoApplicantList, isDeviceTypeMobile } from "../../utils/helpers";
import { InfoOutlined } from "@mui/icons-material";
import TabLayout from "../../components/layouts/TabLayout";
import { ROUTES } from "../../routes/routerConfig";
import ApplicationDetails from "./ApplicationDetails";
import ConsentDetails from "./ConsentDetails";
// import CrmIdChip from "../../components/crmChip";
import NextStepBanner from "../../components/ui/NextStepBanner";
import { PrimaryButtonSecond } from "../../components/ui/Button";
// --- Interfaces ---
interface SummaryProps {
  // Props remain the same, but the component's internal rendering changes
  setCourseNameData?: (val: string) => void;
  pageTypeSummary?: string;
  handleContinue?: (e: any) => void;
  isContinueLoading?: boolean;
}

interface DetailItem {
  label: string;
  name: string;
  value: any;
}
const Summary = ({ setCourseNameData, pageTypeSummary, handleContinue, isContinueLoading }: SummaryProps) => {
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [applicationDetail, setApplicationDetail] = useState<any>(null);
  const [tabsList, setTabsList] = useState<any>(null);
  const [mainApplicantDetails, setMainApplicantDetails] = useState<any>(null);
  const [coApplicantDetails, setCoApplicantDetails] = useState<any>(null);
  const [sanctionDetailsData, setSanctionDetailsData] = useState<any>(null);
  const [breData, setBreDataNew] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [courseName, setCourseName] = useState("");
  const [tabsListLoading, setTabsListLoading] = useState<any>(true);
  const [isProvisionalOfferExpanded, setIsProvisionalOfferExpanded] =
    useState(false);
  const applicationNumber = useSelector(
    (state: RootState) => state.stepper.trackingId
  );
  const [leadId, setLeadId] = useState<string | null>(null);
  const [partnerStartedJourney, setPartnerStartedJourney] = useState<boolean>(false);
  const [salesPersonDetails, setSalesPersonDetails] = useState({
    name: "",
    email: "",
    mobile: "",
  });
  const activeLayoutTab = useSelector(
    (state: RootState) => state.tabs.selectedIndex
  );
  const [baseApplicationDetail, setBaseApplicationDetail] = useState<any[]>([]);
  const [dropdownData, setDropdownData] = useState<any[]>([]);
  const [coApplicantEmploymentType, setCoApplicantEmploymentType] = useState<string>("");
  const [isPayPartyPresent, setIsPayPartyPresent] = useState<boolean>(false);
  const [isPayPartyValue, setIsPayPartyValue] = useState<string>("");


  const formData = useSelector((state: RootState) => state.stepper.formData);
  const { fetchUserApplicationData } = useSelector(
      (state: RootState) => state.stepper
    );


  const applicationStatus = useSelector(
    (state: RootState) => state.tabs.applicationStatus
  );
  const { isTriggerDataEmpty, triggerData }: any = useTriggerData();
  const isApplicant = isTriggerDataEmpty ? true : triggerData?.toUser;

  const documentApplicantType: any = useSelector(
    (state: RootState) => state.stepper.documentApplicantType
  );
 const [documentsHeaderActionsPortalTarget, setDocumentsHeaderActionsPortalTarget] =
    useState<HTMLDivElement | null>(null);
  const findCoApplicantNumber: number = useMemo(() => {
    return Number(documentApplicantType?.split(" ")?.[1]);
  }, [documentApplicantType]);

  const location = useLocation();

  // "Application Details" (tab id 0) is hidden from tabList on this URL, but
  // activeLayoutTab (redux) can still be left at 0 from a previous screen —
  // since content rendering keys off activeLayoutTab directly, force it over
  // to "Documents Upload" so the hidden tab isn't shown by default.
  //
  // The same reset also needs to happen when returning from a Digilocker
  // (document_verified=true) or Account Aggregator (consent=true) redirect
  // while on /application/loan-summary: that hard browser navigation away
  // and back wipes in-memory redux state, so activeLayoutTab reverts to its
  // default 0 even though the user initiated that flow from "Documents
  // Upload" — these query params only ever appear as a result of coming
  // back from one of those third-party flows, so their presence is enough
  // to know the user should land back on that tab.
  const isReturningFromThirdPartyVerification =
    location.search.includes("document_verified=true") ||
    location.search.includes("consent=true");
  const isReturningFromConsentAadhaar =
    location.search.includes("document_verified=true") &&
    Boolean(applicationNumber && localStorage.getItem(`consentAadhaarType:${applicationNumber}`));
  useEffect(() => {
    if (isReturningFromConsentAadhaar && activeLayoutTab !== 2) {
      dispatch(setSelectedIndex(2));
      return;
    }

    if (
      (location.pathname === "/application/upload-document" ||
        triggerData?.toUser === false ||
        isReturningFromThirdPartyVerification) &&
      activeLayoutTab === 0
    ) {
      dispatch(setSelectedIndex(1));
    }
  }, [
    location.pathname,
    location.search,
    activeLayoutTab,
    triggerData?.toUser,
    isReturningFromThirdPartyVerification,
    isReturningFromConsentAadhaar,
    dispatch,
  ]);

  const showApplicationDetailsCard =
    location.pathname === "/application/apply-now" ||
    location.pathname === "/application/summary" ||
    location.pathname === "/application/loan-summary";
  const breadcrumbItems = isXsOrSm
    ? [
      {
        label: `${courseName || ""} [#${applicationNumber || ""}]`,
        link: "",
      },
      { label: "Upload Document", link: "/application/upload-document" },
    ]
    : [
      { label: "Dashboard", path: "/application/existing-application" },
      {
        label: `${courseName || ""} [#${applicationNumber || ""}]`,
        link: "",
      },
      { label: "Upload Document", link: "/application/upload-document" },
    ];

  /**
   * Groups co-applicant fields by applicant number from a flat array of field objects
   *
   * This function processes an array of field objects where each object contains
   * co-applicant data with keys in the format "coApplicant{number}{fieldName}".
   * It extracts the applicant number and field name, then groups all fields
   * belonging to the same co-applicant together.
   *
   * @param {any[]} fieldsList - Array of field objects containing co-applicant data
   * @returns {Record<string, any>} Grouped object where keys are applicant numbers and values are objects containing all fields for that applicant
   *
   * @example
   * Input: [
   *   { "coApplicant1Name": "John" },
   *   { "coApplicant1Email": "john@example.com" },
   *   { "coApplicant2Name": "Jane" }
   * ]
   * Output: {
   *   "1": { "Name": "John", "Email": "john@example.com" },
   *   "2": { "Name": "Jane" }
   * }
   */
  const getCoApplicationDetail = (fieldsList: any) => {
    const grouped: Record<string, any> = {};
    fieldsList?.map((item: any) => {
      const [key, value] = Object.entries(item)[0];
      const match = key.match(/basicCoApplicant(\d+)(.+)/);
      if (match) {
        const [, groupNumber, field] = match;
        const coApplicantKey = `coapplicant${groupNumber}`;
        if (!grouped[coApplicantKey]) grouped[coApplicantKey] = {};
        grouped[coApplicantKey][field] = value;
      }
      return null; // map needs to return something
    });

    return grouped;
  };

  /**
   * Fetches application details and co-applicant information from the API
   *
   * This function retrieves comprehensive application data including:
   * - Basic application details (country, university, institute, course type, loan amount)
   * - Co-applicant information and generates tab names for each co-applicant
   * - Processes and structures the raw API response data for component consumption
   *
   * @async
   * @function handleFetchApplicationDetail
   * @returns {Promise<void>} Updates component state with fetched data
   */
  const handleFetchApplicationDetail = async () => {
    setIsLoading(true);
    // Clear old horizontal steps data before fetching new data
    dispatch(setDisplayHorizontalSteps([]));
    try {
      // Get authentication token from environment variables
      const token: any = process.env.REACT_APP_FETCH_TOKEN;

      // Fetch application form data using the application number
      const res: any = await fetchUserApplicationsFormData(
        token || "",
        applicationNumber
      );

      if (res.status === 200) {
        const applicationData = res?.data?.applications?.[0];
        setLeadId(applicationData?.leadId || "");
        setPartnerStartedJourney(applicationData?.partnerStartedJourney);
        if (!applicationData?.partnerStartedJourney) {
          setSalesPersonDetails({
            name: applicationData?.assignedSMName,
            email: applicationData?.assignedSMEmail,
            mobile: applicationData?.assignedSMMobileNumber,
          });
        }
        if (!applicationData?.partnerStartedJourney) {
          setCourseName(
            applicationData?.horizontalSteps?.[0]?.verticalSteps[1]?.fields?.find(
              (elem: any) => elem?.courseName
            )?.courseName
          );
        }

        const apps = res.data?.applications || [];
        let count = 0;
        // this logic is for getting customized array of horizontalsteps
        let finalSteps: any[] = [];
        for (let i = 0; i < apps.length; i++) {
          const element = apps[i];
          finalSteps = getCombinedApplicationSteps(element, applicationStatus);
          count = finalSteps.filter((val) => val.status === "completed").length;
        }
        dispatch(setDisplayHorizontalSteps(finalSteps));

        dispatch(setTrackingId(applicationData?.submissionId || ""));
        dispatch(setApplicationSource(applicationData?.source || ""));
        const lengthOfHorizontalSteps =
          applicationData?.horizontalSteps?.length;
        let apiFormData: any = {};
        for (let i = 0; i < lengthOfHorizontalSteps; i++) {
          const verticalSteps =
            applicationData?.horizontalSteps?.[i]?.verticalSteps;
          for (let j = 0; j < verticalSteps?.length; j++) {
            const fields = verticalSteps?.[j]?.fields;
            for (let k = 0; k < fields?.length; k++) {
              const field = fields?.[k];
              apiFormData = { ...apiFormData, ...field };
            }
          }
        }

        dispatch(updateFormData(apiFormData));

        let loanAmount =
          applicationData?.horizontalSteps?.[0]?.verticalSteps?.[2]?.fields?.find(
            (elem: any) => elem?.loanAmount
          )?.loanAmount;
        const formattedLoanAmount = parseFloat(loanAmount);

        // Define initial dataJson structure separately
        const initialDataJson: any = [
          {
            label: "Country",
            value:
              applicationData?.horizontalSteps?.[0]?.verticalSteps?.[1]?.fields?.find(
                (elem: any) => elem?.country
              )?.country,
          },
          {
            label: "University",
            value:
              applicationData?.horizontalSteps?.[0]?.verticalSteps?.[1]?.fields?.find(
                (elem: any) => elem?.university
              )?.university,
          },
          {
            label: "Institute",
            value:
              applicationData?.horizontalSteps?.[0]?.verticalSteps?.[1]?.fields?.find(
                (elem: any) => elem?.institute
              )?.institute,
          },
          {
            label: "Course Type",
            value:
              applicationData?.horizontalSteps?.[0]?.verticalSteps?.[1]?.fields?.find(
                (elem: any) => elem?.courseType
              )?.courseType,
          },
          {
            label: "Applied Loan Amount",
            value: !isNaN(formattedLoanAmount)
              ? `₹ ${formattedLoanAmount.toLocaleString()}`
              : "-",
          },
          {
            label: "Created On",
            value: formatDateToShortMonth(applicationData.createdAt),
          },
          // {
          //   label: "Last Action Date",
          //   value: formatDateToShortMonth(
          //     res?.data?.applications?.[0].updatedAt
          //   ),
          // },
          // { label: "Completion Rate", value: "" }, // TODO: Add actual completion rate from API
        ];

        // Add Sales Manager Name ONLY IF partnerStartedJourney is false
        if (applicationData?.partnerStartedJourney === false) {
          initialDataJson.push(
            {
              label: "Partner Name",
              value: applicationData?.partnerName || "-",
            },
            {
              label: "Sales Manager Name",
              value: applicationData?.assignedSMName || "-",
            }
          );
        }
        setBaseApplicationDetail(initialDataJson);
        setApplicationDetail(initialDataJson);

        const fieldsList: any =getCoApplicantList(applicationData);
        const coApplicantNumbers = Array.from(
          new Set(
            fieldsList
              ?.map((obj: any) =>
                Object.keys(obj || {})[0]?.match(/^basicCoApplicant(\d+)FirstName$/)?.[1]
              )
              .filter(Boolean)
              .map((num: string) => Number(num))
          )
        ).sort((a: any, b: any) => a - b);

        // Only use this logic if partnerStartedJourney and leadId are false (use fresh applicationData flags)
        if (
          !(applicationData?.partnerStartedJourney && applicationData?.leadId)
        ) {
          const sortedTabNames = coApplicantNumbers.map(
            (number: any) => `Co-Applicant ${number}`
          );
          sortedTabNames?.unshift("Applicant");
          setTabsList(sortedTabNames);
          const coAppDetails = getCoApplicationDetail(fieldsList);
          setCoApplicantDetails(coAppDetails);
          dispatch(setCoApplicantDetailsRedux(coAppDetails));

          // Extract co-applicant employment type from all fields in applicationData
          if (findCoApplicantNumber && applicationData?.horizontalSteps) {
            let employmentTypeValue = "";
            let employmentIsEmployedValue = "";
            const employmentTypeKey = `employmentCoApplicant${findCoApplicantNumber}employmentType`;
            const payPartyKey = `basicCoApplicant${findCoApplicantNumber}PayParty`;
            const employmentIsEmployedKey = `employmentCoApplicant${findCoApplicantNumber}isEmployed`;

            // Search through all horizontal steps and vertical steps
            for (let i = 0; i < applicationData.horizontalSteps.length; i++) {
              const verticalSteps = applicationData.horizontalSteps[i]?.verticalSteps || [];
              for (let j = 0; j < verticalSteps.length; j++) {
                const fields = verticalSteps[j]?.fields || [];
                const employmentIsEmployedField = fields.find((item: any) => {
                  const key = Object.keys(item)[0];
                  return key === employmentIsEmployedKey;
                });
                const employmentTypeField = fields.find((item: any) => {
                  const key = Object.keys(item)[0];
                  return key === employmentTypeKey;
                });
                if (employmentTypeField) {
                  employmentTypeValue = Object.values(employmentTypeField)[0] as string;
                  employmentIsEmployedValue = Object.values(employmentIsEmployedField)[0] as string;

                  const payPartyField = fields.find((item: any) => {
                    const key = Object.keys(item)[0];
                    return key === payPartyKey;
                  });

                  if (payPartyField) {
                    setIsPayPartyPresent(true);
                    setIsPayPartyValue(Object.values(payPartyField)[0] as string);
                  }
                  break;
                }
              }
              if (employmentTypeValue) break;
            }
            setCoApplicantEmploymentType(employmentIsEmployedValue == "Yes" ? employmentTypeValue : "");
          }
        }

        dispatch(setFetchUserApplicationData(applicationData));

        // setting status which will be used for display validation of summary tabs and status in horizontalsteps, which is application specific
        dispatch(setApplicationStatus(res?.data?.applications?.[0]?.status));
        // setting assigned sales person id, which is application specific, will be used for display validation of Connect button and Chat
        dispatch(setSalesUserId(applicationData?.assignedSMId));

        // setting firsttime view only popup flags which will get displayed in summary page
        dispatch(
          setOpenFirstTimeOnlyPopup({
            // isSanctionedPopupViewed: applicationData?.SanctionedPopUp,
            isDisbursedPopupViewed: applicationData?.DisbursementPopUp,
            isConnectWithSalesPopupViewed:
              applicationData?.ConnectWithSalesPopUp,
          })
        );
      }
    } catch (error) {
      
    } finally {
      setIsLoading(false);
    }
  };


  const handleFetchBreapi = async () => {
    try {
      const payload = {
        applicationId: applicationNumber,
        name: "getProvisionalOffer",
      };
      const res: any = await eligibilityCheckServices.fetchBreApi(payload);
      if (res?.status == 200) {
        setBreDataNew(res?.data?.data);
        dispatch(setBreData(res?.data?.data));
        dispatch(
          setIsProvisionalOffer(
            res?.data?.data?.Provisional_Offer?.toLowerCase() === "yes"
          )
        );
      }
    } catch (error) {
      setBreDataNew(null);
    }
  };

  // fetching partner lead details to show Partnar and Sales person details in summary page
  const handleFetchPartnerLeadDetailsApi = async () => {
    if (!leadId || baseApplicationDetail.length === 0) {
      return;
    }
    try {
      const payload = {
        leadId: leadId,
        trackingId: applicationNumber,
      };
      const res: any = await eligibilityCheckServices.partnerGetLeadDetailsApi(
        payload
      );
      const partnerDetails = res.data;

      if (partnerStartedJourney && partnerDetails) {
        dispatch(setPartnerDetails(partnerDetails?.data || {}));
        const appDetails: DetailItem[] =
          partnerDetails?.data?.applicationDetails || [];

        const getValue = (list: DetailItem[], key: string): string =>
          list.find((item) => item.name === key)?.value ?? "";

        setSalesPersonDetails({
          name: getValue(appDetails, "salesManagerName"),
          email: getValue(appDetails, "salesManagerEmail"),
          mobile: getValue(appDetails, "salesManagerMobile"),
        });
        setCourseName(getValue(appDetails, "courseName"));
        if (setCourseNameData) {
          setCourseNameData(getValue(appDetails, "courseName") || "")
        }
        const newPartnerData = [
          {
            label: "Partner Name",
            value: getValue(appDetails, "partnerName") || "-",
          },
          {
            label: "Sales Manager Name",
            value: getValue(appDetails, "salesManagerName") || "-",
          },
        ];

        // Merge the new partner data with the applicationDetails from partnerDetails
        const filteredApplicationDetails = (
          partnerDetails?.data?.applicationDetails || []
        ).filter((item: any) => {
          const name = (item.name || "").toLowerCase();
          return (
            name !== "applicationid" &&
            name !== "salesmanagername" &&
            name !== "salesmanageremail" &&
            name !== "salesmanagermobile" &&
            name !== "coursename" &&
            name !== "status"
          );
        });
        const finalDataJson = [
          ...filteredApplicationDetails,
          ...newPartnerData,
        ];
        setApplicationDetail(finalDataJson);
        if (partnerDetails?.data?.applicantDetails) {
          setMainApplicantDetails(partnerDetails.data.applicantDetails);
        }

        if (partnerDetails?.data?.sanctionDetails) {
          setSanctionDetailsData(partnerDetails.data.sanctionDetails);
        }

        if (partnerDetails?.data?.coApplicantDetails) {
          // Set coApplicantDetails from partnerDetails using a new getCoApplicationDetail function
          const partnerCoApplicantDetails =
            partnerDetails.data.coApplicantDetails;

          const getPartnerCoApplicationDetail = (detailsArr: any[]) => {
            const grouped: Record<string, any> = {};
            detailsArr.forEach((obj: any) => {
              const key = Object.keys(obj)[0];
              const arr = obj[key];
              grouped[key] = {};
              arr.forEach((fieldObj: any) => {
                grouped[key][fieldObj.name] = fieldObj.value;
              });
            });

            return grouped;
          };

          const partnerCoAppDetails = getPartnerCoApplicationDetail(partnerCoApplicantDetails);
          setCoApplicantDetails(partnerCoAppDetails);
          dispatch(setCoApplicantDetailsRedux(partnerCoAppDetails));

          // Extract co-applicant employment type from partner co-applicant details
          if (findCoApplicantNumber && partnerCoApplicantDetails) {
            const coApplicantKey = `coapplicant${findCoApplicantNumber}`;
            const coApplicantData = partnerCoApplicantDetails.find((item: any) => {
              const key = Object.keys(item)[0];
              return key.toLowerCase() === coApplicantKey.toLowerCase();
            });
            if (coApplicantData) {
              const coApplicantFields = coApplicantData[Object.keys(coApplicantData)[0]];
              if (Array.isArray(coApplicantFields)) {
                const employmentTypeField = coApplicantFields.find(
                  (field: any) => field.name === "employmentType" || field.name?.toLowerCase().includes("employmenttype")
                );
                if (employmentTypeField && employmentTypeField.value) {
                  setCoApplicantEmploymentType(employmentTypeField.value);
                }
              }
            }
          }

          // Tab names logic remains unchanged
          const coApplicantDetailsArr = partnerDetails.data.coApplicantDetails;
          const coApplicantTabNameList = [];
          for (let i = 0; i < coApplicantDetailsArr.length; i++) {
            const key = Object.keys(coApplicantDetailsArr[i])[0];
            const numberMatch = key.match(/coapplicant(\d+)/i);
            const number = numberMatch ? parseInt(numberMatch[1]) : i + 1;
            coApplicantTabNameList.push({
              number,
              name: `Co-Applicant ${number}`,
            });
          }
          coApplicantTabNameList.sort((a, b) => a.number - b.number);
          const sortedTabNames = coApplicantTabNameList.map(
            (item) => item.name
          );
          sortedTabNames.unshift("Applicant");
          setTabsList(sortedTabNames);
        } else {
          // When partner response has no co-applicants, default to Applicant tab only
          setTabsList(["Applicant"]);
        }
      }
    } catch (error) {
    }
  };

  // removed "findCoApplicantNumber" from dependency array to avoid unnecessary API calls when user switches between co-applicant tabs, now it will only call when applicationNumber changes or when partnerStartedJourney and leadId flags change which indicates that we have new data to fetch for co-applicant details
  useEffect(() => {
    handleFetchApplicationDetail();
    if(applicationNumber) {
      handleFetchBreapi();
    }
  }, [applicationNumber, findCoApplicantNumber,activeLayoutTab]);

  // removed "baseApplicationDetail" and "findCoApplicantNumber" from dependency array to avoid unnecessary API calls, now it will only call when partnerStartedJourney and leadId flags change which indicates that we have new data to fetch for co-applicant details
  useEffect(() => {
    if (partnerStartedJourney && leadId) {
      setApplicationDetail([]);
      const fetchPartnerDetails = async () => {
        await handleFetchPartnerLeadDetailsApi();
      };
      fetchPartnerDetails();
    }
  }, [partnerStartedJourney, leadId, baseApplicationDetail, findCoApplicantNumber]);


  // const handleCoApplicantDetailsAsperTab = () => {
  //   if (findCoApplicantNumber && formData && typeof formData === "object" && !Array.isArray(formData)) {
  //     const employmentTypeKey = `employmentCoApplicant${findCoApplicantNumber}employmentType`;
  //     const payPartyKey = `basicCoApplicant${findCoApplicantNumber}PayParty`;
  //     const employmentIsEmployedKey = `employmentCoApplicant${findCoApplicantNumber}isEmployed`;

  //     const employmentTypeValue = (formData[employmentTypeKey] ?? "") as string;
  //     const employmentIsEmployedValue = (formData[employmentIsEmployedKey] ?? "") as string;
  //     const payPartyVal = formData[payPartyKey];

  //     setCoApplicantEmploymentType(employmentIsEmployedValue === "Yes" ? employmentTypeValue : "");
  //     if (payPartyVal !== undefined && payPartyVal !== "") {
  //       setIsPayPartyPresent(true);
  //       setIsPayPartyValue(String(payPartyVal));
  //     } else {
  //       setIsPayPartyPresent(false);
  //       setIsPayPartyValue("");
  //     }
  //   }
  // };


  // useEffect(() => {
  //   handleCoApplicantDetailsAsperTab();
  // }, [applicationNumber, findCoApplicantNumber, formData]);


  const handleUploadDocument = async () => {
    try {
      const token: any = process.env.REACT_APP_FETCH_TOKEN;
      const res: any = await fetchUserApplicationsFormData(
        token,
        applicationNumber
      );

      const apps = res.data?.applications || [];
      let apiFormData: any = {};
      for (let i = 0; i < apps?.[0]?.horizontalSteps?.length; i++) {
        const verticalSteps = apps?.[0]?.horizontalSteps?.[i]?.verticalSteps;
        for (let j = 0; j < verticalSteps?.length; j++) {
          const fields = verticalSteps?.[j]?.fields;
          for (let k = 0; k < fields?.length; k++) {
            const field = fields?.[k];
            apiFormData = { ...apiFormData, ...field };
          }
        }
      }

      dispatch(updateFormData(apiFormData));
      dispatch(setTrackingId(applicationNumber));
      navigate("/application/upload-document");
    } catch (e) {
    }
  };

  // displaying sales manager displaying inside tooltip onhover of i btn which is just on side of Sales manager name label
  const SalespersonTooltipContent = (
    <>
      <Typography variant="caption" display="block">
          Email: {salesPersonDetails?.email || "-"}
        </Typography>
      <Typography variant="caption" display="block">
          Mobile: {salesPersonDetails?.mobile || "-"}
        </Typography>
    </>
  );

  const handleShowTriggerLinkCoApplicant = useMemo(() => {
    const key: any = `coapplicant${findCoApplicantNumber}`;
    const co: any = coApplicantDetails?.[key];
    return (
      <>
        <Typography
          component="div"
          sx={{ flexShrink: 0, fontSize: "13px", color: "#5d586c" }}
        >
          <span
            style={{
              fontWeight: "600",
              fontSize: "15px",
              color: "#1F2233",
              marginRight: "8px",
              display: "flex",
              flexDirection: isXsOrSm ? "column" : "row",
              alignItems: isXsOrSm ? "flex-start" : "center",
              gap: isXsOrSm ? "4px" : "8px",
            }}
          >
            {/* Name Block */}
            <span>
              <span className="section-heading">Name:</span>{" "}
              <span style={{fontSize:"15px"}}>{`${co?.FirstName} ${co?.LastName}` || co?.coApplicantName || ""} </span>
            </span>

            {/* Separator - Only show on Desktop */}
            {!isXsOrSm && <span>|</span>}

            {/* Email Block */}
            <span>
              <span className="section-heading">Email:</span>{" "}
              <span style={{fontSize:"15px"}}>{co?.Email || co?.coApplicantEmail || ""}</span>
            </span>

            {/* Phone Block */}
            <span>
              <span className="section-heading">Phone:</span>{" "}
              <span style={{fontSize:"13px"}}>{co?.PhoneNumber || co?.coApplicantPhone || ""}</span>
            </span>
          </span>
        </Typography>
      </>
    );
  }, [coApplicantDetails, findCoApplicantNumber, isXsOrSm]);

  const normalizedProvisionalOffer = breData?.Provisional_Offer?.toLowerCase();
  const isSanctioned = applicationStatus?.trim().toLowerCase() === "sanctioned";
  const shouldShowValue = !partnerStartedJourney || isSanctioned;

  const canToggleProvisional =
    normalizedProvisionalOffer === "yes" ||
    normalizedProvisionalOffer === undefined ||
    (partnerStartedJourney &&
      normalizedProvisionalOffer === "no" &&
      applicationStatus !== APP_STATUS.DISBURSED &&
      applicationStatus !== APP_STATUS.ELIGIBILITY_FAILED &&
      applicationStatus !== APP_STATUS.APPLICATION_REJECTED &&
      applicationStatus !== APP_STATUS.APPLICATION_CLOSED &&
      applicationStatus !== APP_STATUS.DECLINED_BY_CREDIT);

  const message = getApplicationMessage(
    applicationStatus,
    normalizedProvisionalOffer,
    partnerStartedJourney
  );

  const loanAmountSanctioned = sanctionDetailsData?.find(
    (item: any) => item?.name === "loanAmountSanctioned",
  )?.value;
  const maxTenure = sanctionDetailsData?.find(
    (item: any) => item?.name === "maxTenure",
  )?.value;
  const vasAmount = sanctionDetailsData?.find(
    (item: any) => item?.name === "vasAmount",
  )?.value;
  const currency = sanctionDetailsData?.find(
    (item: any) => item?.name === "currency",
  )?.value;

  const getRepaymentLabel = (type: string) => {
   let val= typeof type === "string"  ? type?.toUpperCase():type;
    switch (val) {
      case "PINT":
        return "Partial interest";
      case "SINT":
        return "Simple interest";
      case "EMI":
        return "EMI";
      default:
        return type || "-";
    }
  };

  // Fixed helper to return specific content or null
  const getTooltipContent = (type: string) => {
    let val= typeof type === "string" ?type?.toUpperCase():type;
    switch (val) {
      case "PINT":
        return (
          <Typography variant="caption">
            <b>Partial Interest:</b> Pay a portion of the interest now; the rest is added to your principal for the next stage of your loan.
          </Typography>
        );
      case "SINT":
        return (
          <Typography variant="caption">
            <b>Simple Interest:</b> Pay full interest now; your principal repayment begins after the moratorium.
          </Typography>
        );
      default:
        return null;
    }
  };
  const activeTooltip = getTooltipContent(breData?.Repayment_Type);

  const finalLoanAmountValue =
    partnerStartedJourney && isSanctioned
      ? loanAmountSanctioned || "-"
      : (shouldShowValue && breData?.Max_Loan_Amount) || "-";

  const finalCurrencyValue =
    partnerStartedJourney && isSanctioned
      ? currency || "-"
      : (shouldShowValue && breData?.currency) || "-";

  const finalVasAmountValue =
    partnerStartedJourney && isSanctioned ? vasAmount || "-" : "-";

  const finalMaxTenureValue =
    partnerStartedJourney && isSanctioned
      ? maxTenure || "-"
      : shouldShowValue && breData?.Max_Tenure
        ? `${breData?.Max_Tenure} Months`
        : "-";

  const formatCurrency = (value: any) => {
    const num = parseFloat(value);
    if (isNaN(num)) return "-";
    return `₹ ${new Intl.NumberFormat('en-IN').format(num)}`;
  };
  
const handleViewTabSection = useMemo(() => {
    if (activeLayoutTab === 0) {
      return (
        <>
          {/* <UploadDocument
            isLoading={isLoading}
            tabsList={tabsList}
            tabsListLoading={tabsListLoading}
            applicationDetail={applicationDetail}
            mainApplicantDetails={mainApplicantDetails}
            coApplicantDetails={coApplicantDetails}
            handleFetchApplicationDetail={handleFetchApplicationDetail}
            partnerStartedJourney={partnerStartedJourney}
            setPartnerStartedJourney={setPartnerStartedJourney}
            headerActionsPortalTarget={documentsHeaderActionsPortalTarget}
            headerActionsOnly={true}
          /> */}
          <ApplicationDetails pageTypeSummary={pageTypeSummary} />
        </>
      );
    }
    if (activeLayoutTab === 1) {
      return (<>
        <UploadDocument
          isLoading={isLoading}
          tabsList={tabsList}
          tabsListLoading={tabsListLoading}
          applicationDetail={applicationDetail}
          mainApplicantDetails={mainApplicantDetails}
          coApplicantDetails={coApplicantDetails}
          handleFetchApplicationDetail={handleFetchApplicationDetail}
          partnerStartedJourney={partnerStartedJourney}
          setPartnerStartedJourney={setPartnerStartedJourney}
          headerActionsPortalTarget={documentsHeaderActionsPortalTarget}
        />
      </>);
    }
    if (activeLayoutTab === 2) {
      return (<>
        <div className="card-contaner">
          <Typography className="card-section-hedging" sx={{ mb: 2,color:"black" }}>
            Consent & Verification
          </Typography>
          <ConsentDetails
            formData={formData}
            mainApplicantDetails={mainApplicantDetails}
            coApplicantDetails={coApplicantDetails}
            trackingId={applicationNumber || ""}
            onContinue={() => dispatch(setSelectedIndex(0))}
            partnerStartedJourney={partnerStartedJourney}
            pageTypeSummary={pageTypeSummary}
          />
        </div>
      </>);
    }
  }, [activeLayoutTab,
    isLoading,
    tabsList,
    tabsListLoading,
    applicationDetail,
    mainApplicantDetails,
    coApplicantDetails,
    partnerStartedJourney,
    setPartnerStartedJourney,
    handleFetchApplicationDetail,
    documentsHeaderActionsPortalTarget,
    formData,
    applicationNumber,
  ])
  useEffect(() => {
    const fetchSystemValues = async () => {
      try {
        const systemValuesResponse =
          await eligibilityCheckServices.getSystemValues();

        if (systemValuesResponse.data?.status === "success") {
          const systemValues = systemValuesResponse.data.data;

          // Filter and process Language Exam Types
          const languageExamTypes = systemValues
            .filter(
              (item: any) => item.name === "LANGUAGE_EXAM_TYPE" && item.active
            )
            .map((item: any) => ({
              value: item.code,
              label: item.value,
              id: item.serialId,
            }));

          // Filter and process Entrance Exam Types
          const entranceExamTypes = systemValues
            .filter(
              (item: any) => item.name === "ENTRANCE_EXAM_TYPE" && item.active
            )
            .map((item: any) => ({
              value: item.code,
              label: item.value,
              id: item.serialId,
            }));

          // Update dropdown data with system values
          setDropdownData([...languageExamTypes, ...entranceExamTypes]);
        }
      } catch (error) {
        console.error("Error fetching system values:", error);
      }
    };
    fetchSystemValues();
  }, []);
  
  const tabList = [
    (triggerData?.toUser!==false &&
      location.pathname !== "/application/upload-document") &&
      { id: 0, label: "Application Details" },
    { id: 1, label: "Documents Upload" },
    { id: 2, label: "Consent & Verification" },
  ].filter(Boolean);
  return (
    <>
      <>
        {location.pathname == "/application/upload-document" && (
          <Box sx={{ width: "100%", padding: "0px 0px 18px 0px",mt: isDeviceTypeMobile(isXsOrSm)? 10: 0 }}>
            <Breadcrumbs items={breadcrumbItems} onBackClick={() => navigate("/application/existing-application")} />
          </Box>
        )}
        {pageTypeSummary!="loansummary" && 
          <Grid item xs={12} md={isApplicant ? 7.5 : 12} sx={{ mb: 1.5 }}>
            {!isApplicant && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "start",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{
                      mt: isApplicant ? 3 : 0,
                      mb: 3,
                      fontWeight: 600,
                      fontSize: "18px",
                      color: "#1F2233",
                    }}
                  >
                    <span className="trigger-link-heading">Application No :</span>{" "}
                    <span style={{fontSize:"15px"}}>#{applicationNumber}</span>
                    
                  </Typography>
                  <img
                    src={Logo}
                    alt="Logo"
                    style={{
                      height: "40px",
                      objectFit: "contain",
                      marginLeft: "16px",
                    }}
                  />
                </Box>
              </>
            )}

            {!isApplicant && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {handleShowTriggerLinkCoApplicant}
                </Box>
                <br />
              </>
            )}
          </Grid>
        }

        {showApplicationDetailsCard && pageTypeSummary!="loansummary" &&(
          <Box>
            {isLoading ? (
              <Box
                sx={{
                  width: "100%",
                  margin: "0 auto",
                  textAlign: "center",
                }}
              >
                <div className="card-contaner">
                  {/* Application Details */}
                  <Grid container spacing={3} sx={{ my: 2 }}>
                    <Grid item xs={12} md={8}>
                      <Grid container rowSpacing={2} columnSpacing={4}>
                        {Array.from({ length: 8 }).map((_, index) => (
                          <Grid
                            item
                            xs={6}
                            sm={3}
                            key={index}
                            sx={{ textAlign: "left" }}
                          >
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                              width="80%"
                            />
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                              width="60%"
                            />
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <div className="right-side-box">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Skeleton
                            variant="rectangular"
                            width={24}
                            height={24}
                            sx={{ mr: 1, borderRadius: "5px" }}
                          />
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "1.1rem" }}
                            width="80%"
                          />
                        </Box>
                        <Grid container spacing={2} sx={{ textAlign: "left" }}>
                          {Array.from({ length: 4 }).map((_, index) => (
                            <Grid item xs={12} sm={6} key={index}>
                              <Skeleton
                                variant="text"
                                sx={{ fontSize: "0.9rem" }}
                                width="70%"
                              />
                              <Skeleton
                                variant="text"
                                sx={{ fontSize: "1rem" }}
                                width="50%"
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </div>
                    </Grid>
                  </Grid>
                </div>
              </Box>
            ) : (
              <div className="card-contaner">
                {isXsOrSm ? (
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isXsOrSm ? "column" : "row",
                        justifyContent: "space-between",
                        alignItems: isXsOrSm ? "flex-start" : "center",
                        gap: 0.5,
                        width: "100%",
                        mb: 2,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                          width: "100%",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <TruncatedTextWithTooltip
                            style={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#333",
                            }}
                            text={courseName || "-"}
                            // limit={20}
                          />
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 600,
                              color: "#333",
                            }}
                          >
                            [#{applicationNumber}]
                          </Typography>
                        </Box>
                        {/* {fetchUserApplicationData && fetchUserApplicationData?.externalId && 
                        <CrmIdChip crmId={fetchUserApplicationData?.externalId} label="App Id:" />} */}
                        <Box sx={{ flexShrink: 0 }}>
                          <Chip
                            label={applicationStatus}
                            className="card-contaner-status"
                            sx={{
                              maxWidth: "100%", 
                              height: "auto",
                              "& .MuiChip-label": {
                                display: "block",
                                whiteSpace: "normal",
                              },
                            }}
                          />
                        </Box>
                      </Box>
                      
                    </Box>

                    <Grid
                      container
                      spacing={3}
                      className="card-contaner-section"
                    >
                      {/* Increased spacing between details and offer box */}
                      <Grid item xs={12} md={8}>
                        <Grid container rowSpacing={2} columnSpacing={4}>
                          {/* Adjusted spacing for better alignment */}
                          {applicationDetail == null || applicationDetail?.length === 0
                            ? Array.from({ length: 8 }).map((_, index) => (
                                <Grid item xs={6} sm={3} key={index}>
                                  <Skeleton
                                    variant="text"
                                    sx={{ fontSize: "0.85rem" }}
                                    width="70%"
                                  />
                                  <Skeleton
                                    variant="text"
                                    sx={{ fontSize: "1rem" }}
                                    width="55%"
                                  />
                                </Grid>
                              ))
                            : applicationDetail
                            ?.filter((item: any) => {
                              const skipFields = [
                                "status",
                                "applicationid",
                                "salesmanageremail",
                                "salesmanagermobile",
                                "coursename",
                                "coursecategory",
                                "admissionstatus",
                                "product",
                                // "appliedloanamount",
                                "totalcost",
                              ].map((i) => i.toLowerCase());
                              const hiddenOnMobileFields = [
                                "institute",
                                "course type",
                              ].map((i) => i.toLowerCase());
                              return !skipFields.includes(
                                item.name?.toLowerCase(),
                              ) && !(isXsOrSm && hiddenOnMobileFields.includes(
                                item.label?.toLowerCase(),
                              ));
                            })
                            .map((item: any, index: any) => {
                              // Format createdOn date if present
                              const formattedItem =
                                item.name === "createdOn" && item.value
                                  ? {
                                    ...item,
                                    value: formatDateToShortMonth(item.value),
                                  }
                                  : item;
                              return (
                                <Grid item xs={6} sm={3} key={index}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                    }}
                                  >
                                    <Typography className="card-contaner-lable">
                                      {item.label
                                        ?.replace(/[_-]+/g, " ")
                                        .replace(/([A-Z])/g, " $1")
                                        .replace(/\s+/g, " ")
                                        .replace(/^./, (str: any) =>
                                          str.toUpperCase(),
                                        )
                                        .split(" ")
                                        .map(
                                          (word: any) =>
                                            word.charAt(0).toUpperCase() +
                                            word.slice(1),
                                        )
                                        .join(" ")
                                        .trim()}
                                    </Typography>

                                    {/* mobile view */}
                                    {item.label === "Sales Manager Name" && (
                                      <Tooltip
                                        title={SalespersonTooltipContent}
                                        placement="top-start" 
                                        arrow
                                        enterTouchDelay={0}
                                        leaveTouchDelay={3000}
                                      >
                                        <IconButton
                                          size="small"
                                          sx={{
                                            mb: "10px",
                                            padding: 0,
                                            "&:hover": {
                                              background: "transparent",
                                            },
                                          }}
                                          aria-label="Salesperson contact details"
                                        >
                                          <InfoOutlinedIcon
                                            sx={{
                                              fontSize: "14px",
                                              color: "#a5a3ae",
                                              cursor: "pointer",
                                            }}
                                          />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>

                                  <Typography sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Typography style={{ fontSize: '13px', color: '#6F6B7D' }}>{item.name === 'appliedLoanAmount' ? '₹ ' : ''}</Typography>
                                    <TruncatedTextWithTooltip
                                      className="card-contaner-value"
                                      text={formattedItem.value}
                                      // limit={15}
                                    />
                                  </Typography>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </Grid>

                      <Grid item xs={12} md={4} sx={{ display: isApplicant ? "block" : "none" }}>
                        <div
                          className={`${
                            !partnerStartedJourney &&
                            normalizedProvisionalOffer === "yes"
                              ? "right-side-box-provision"
                              : partnerStartedJourney &&
                                  (applicationStatus === APP_STATUS.DISBURSED ||
                                    applicationStatus === APP_STATUS.SANCTIONED)
                                ? "right-side-box-provision"
                                : "right-side-box"
                          } `}
                          style={isXsOrSm ? {} : {minHeight: "170px" }}
                        >
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            onClick={
                              canToggleProvisional
                                ? () =>
                                    setIsProvisionalOfferExpanded(
                                      !isProvisionalOfferExpanded,
                                    )
                                : undefined
                            }
                            sx={{
                              cursor: canToggleProvisional
                                ? "pointer"
                                : "default",
                            }}
                          >
                            <Grid
                              sx={{ display: "flex", alignItems: "center" }}
                            >
                              {applicationStatus === APP_STATUS.SANCTIONED ? (
                                <img
                                  src={
                                    !partnerStartedJourney &&
                                    normalizedProvisionalOffer === "yes"
                                      ? textFile
                                      : LockSquare
                                  }
                                  alt="lock-square"
                                />
                              ) : !partnerStartedJourney &&
                                normalizedProvisionalOffer === "no" ? null : (
                                applicationStatus !== APP_STATUS.DISBURSED &&
                                applicationStatus !==
                                  APP_STATUS.ELIGIBILITY_FAILED &&
                                applicationStatus !==
                                  APP_STATUS.APPLICATION_REJECTED &&
                                applicationStatus !==
                                  APP_STATUS.APPLICATION_CLOSED &&
                                applicationStatus !==
                                  APP_STATUS.DECLINED_BY_CREDIT && (
                                  <img
                                    src={
                                      !partnerStartedJourney &&
                                      normalizedProvisionalOffer === "yes"
                                        ? textFile
                                        : LockSquare
                                    }
                                    alt="lock-square"
                                  />
                                )
                              )}
                              {/* Icon */}
                              <Typography
                                // className="right-side-heading"
                                sx={{
                                  ml: 1.5,
                                  mr: 1,
                                  fontSize: "15px",
                                  fontWeight: 600,
                                  color: "#333",
                                }}
                              >
                                {message}
                              </Typography>
                            </Grid>
                            {applicationStatus === APP_STATUS.SANCTIONED ? (
                              <Box sx={{ cursor: "pointer", pt: 1 }}>
                                {isProvisionalOfferExpanded ? (
                                  <KeyboardArrowUpIcon
                                    sx={{ color: "rgba(0, 179, 181, 1)" }}
                                  />
                                ) : (
                                  <KeyboardArrowDownIcon
                                    sx={{ color: "rgba(0, 179, 181, 1)" }}
                                  />
                                )}
                              </Box>
                            ) : !partnerStartedJourney &&
                              normalizedProvisionalOffer === "no" ? null : (
                              applicationStatus !== APP_STATUS.DISBURSED &&
                              applicationStatus !==
                                APP_STATUS.ELIGIBILITY_FAILED &&
                              applicationStatus !==
                                APP_STATUS.APPLICATION_REJECTED &&
                              applicationStatus !==
                                APP_STATUS.APPLICATION_CLOSED &&
                              applicationStatus !==
                                APP_STATUS.DECLINED_BY_CREDIT && (
                                <Box sx={{ cursor: "pointer", pt: 1 }}>
                                  {isProvisionalOfferExpanded ? (
                                    <KeyboardArrowUpIcon
                                      sx={{ color: "rgba(0, 179, 181, 1)" }}
                                    />
                                  ) : (
                                    <KeyboardArrowDownIcon
                                      sx={{ color: "rgba(0, 179, 181, 1)" }}
                                    />
                                  )}
                                </Box>
                              )
                            )}
                          </Box>
                          {isProvisionalOfferExpanded &&
                          applicationStatus === APP_STATUS.SANCTIONED ? (
                            <Grid container spacing={2} mt={1}>
                              <Grid item xs={6}>
                                <Typography className="right-side-lable">
                                  {partnerStartedJourney
                                    ? "Loan Amount"
                                    : "Offer loan amount"}
                                </Typography>
                                <Typography className="right-side-value">
                                  {formatCurrency(finalLoanAmountValue)}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography className="right-side-lable">
                                  Tenure
                                </Typography>
                                <Typography className="right-side-value">
                                  {finalMaxTenureValue}
                                </Typography>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography className="right-side-lable">
                                  {partnerStartedJourney && isSanctioned
                                    ? "VAS Amount"
                                    : "Repayment type"}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography className="right-side-value" sx={{ textTransform: 'capitalize' }}>
                                    {partnerStartedJourney && isSanctioned
                                      ? formatCurrency(finalVasAmountValue)
                                      : (shouldShowValue && getRepaymentLabel(breData?.Repayment_Type)) || "-"
                                    }
                                  </Typography>
                                  
                                  {!partnerStartedJourney && breData?.Repayment_Type && (
                                    <Tooltip 
                                      title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>} 
                                      arrow 
                                      placement="top"
                                      enterTouchDelay={0}
                                      leaveTouchDelay={3000} 
                                    >
                                      <InfoOutlined 
                                        sx={{ 
                                          fontSize: "16px", 
                                          cursor: "pointer", 
                                          color: "action.active",
                                          userSelect: "auto",
                                          WebkitUserSelect: "auto" 
                                        }} 
                                      />
                                    </Tooltip>
                                  )}
                                </Box>
                              </Grid>

                              <Grid item xs={6}>
                                <Typography className="right-side-lable">
                                  Currency
                                </Typography>
                                <Typography className="right-side-value">
                                  {finalCurrencyValue}
                                </Typography>
                              </Grid>
                            </Grid>
                          ) : !partnerStartedJourney &&
                            normalizedProvisionalOffer === "no" ? null : (
                            isProvisionalOfferExpanded &&
                            applicationStatus !== APP_STATUS.DISBURSED &&
                            applicationStatus !==
                              APP_STATUS.ELIGIBILITY_FAILED &&
                            applicationStatus !==
                              APP_STATUS.APPLICATION_REJECTED &&
                            applicationStatus !==
                              APP_STATUS.APPLICATION_CLOSED &&
                            applicationStatus !==
                              APP_STATUS.DECLINED_BY_CREDIT && (
                              <Grid container spacing={2} mt={1}>
                                <Grid item xs={6}>
                                  <Typography className="right-side-lable">
                                    {partnerStartedJourney
                                      ? "Loan Amount"
                                      : "Offer loan amount"}
                                  </Typography>
                                  <Typography className="right-side-value">
                                    {formatCurrency(finalLoanAmountValue)}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography className="right-side-lable">
                                    Tenure
                                  </Typography>
                                  <Typography className="right-side-value">
                                    {finalMaxTenureValue}
                                  </Typography>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography className="right-side-lable">
                                    {partnerStartedJourney && isSanctioned
                                      ? "VAS Amount"
                                      : "Repayment type"}
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Typography className="right-side-value" sx={{ textTransform: 'capitalize' }}>
                                      {partnerStartedJourney && isSanctioned
                                        ? formatCurrency(finalVasAmountValue)
                                        : (shouldShowValue && getRepaymentLabel(breData?.Repayment_Type)) || "-"
                                      }
                                    </Typography>
                                    
                                    {!partnerStartedJourney && breData?.Repayment_Type && (
                                      <Tooltip 
                                        title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>} 
                                        arrow 
                                        placement="top"
                                        enterTouchDelay={0}
                                        leaveTouchDelay={3000} 
                                      >
                                        <InfoOutlined 
                                          sx={{ 
                                            fontSize: "16px", 
                                            cursor: "pointer", 
                                            color: "action.active",
                                            userSelect: "auto",
                                            WebkitUserSelect: "auto" 
                                          }} 
                                        />
                                      </Tooltip>
                                    )}
                                  </Box>
                                </Grid>

                                <Grid item xs={6}>
                                  <Typography className="right-side-lable">
                                    Currency
                                  </Typography>
                                  <Typography className="right-side-value">
                                    {finalCurrencyValue}
                                  </Typography>
                                </Grid>
                              </Grid>
                            )
                          )}
                        </div>
                      </Grid>
                    </Grid>
                  </Box>
                ) : (
                  <Grid container spacing={3} className="card-contaner-section">
                    <Grid
                      item
                      xs={12}
                      md={isApplicant ? 7.5 : 12}
                      sx={{ mb: 1.5 }}
                    >
                      <Typography className="card-section-hedging">
                        Application Details
                      </Typography>

                      <Grid container rowSpacing={2} columnSpacing={4}>
                        <Grid item xs={12}>
                          <Grid container rowSpacing={2} columnSpacing={4}>
                            {applicationDetail == null ||
                              applicationDetail?.length === 0
                              ? Array.from({ length: 8 }).map((_, index) => (
                                <Grid item xs={12} sm={6} md={3} key={index}>
                                  <Skeleton
                                    variant="text"
                                    sx={{ fontSize: "1rem" }}
                                    width="80%"
                                  />
                                  <Skeleton
                                    variant="text"
                                    sx={{ fontSize: "1rem" }}
                                    width="60%"
                                  />
                                </Grid>
                              ))
                              : applicationDetail
                                  ?.filter((item: any) => {
                                    const skipFields = [
                                      "status",
                                      "applicationid",
                                      "salesmanageremail",
                                      "salesmanagermobile",
                                      "coursename",
                                      "coursecategory",
                                      "admissionstatus",
                                      "product",
                                      // "appliedloanamount",
                                      "totalcost",
                                    ].map((i) => i.toLowerCase());
                                    return !skipFields.includes(
                                      item.name?.toLowerCase(),
                                    );
                                  })
                                  .map((item: any, index: any) => {
                                    // Format createdOn date if present
                                    const formattedItem =
                                      item.name === "createdOn" && item.value
                                        ? {
                                            ...item,
                                            value: formatDateToShortMonth(
                                              item.value,
                                            ),
                                          }
                                        : item;
                                    return (
                                      <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                        md={3}
                                        key={item.label}
                                      >
                                        <DetailRow
                                          item={formattedItem}
                                          showInfoIcon={
                                            item.label === "Sales Manager Name"
                                              ? true
                                              : false
                                          }
                                          salespersonTooltipContent={
                                            SalespersonTooltipContent
                                          }
                                        />
                                      </Grid>
                                    );
                                  })}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      md={4.5}
                      sx={{ display: isApplicant ? "flex" : "none", gap: 1.5 }}
                    >
                      <Grid item sx={{}}>
                        <CustomDivider isVertical={true} />
                      </Grid>

                      {applicationStatus === APP_STATUS.SANCTIONED ? (
                        <div
                          className={
                            (!partnerStartedJourney &&
                              normalizedProvisionalOffer === "yes") ||
                            applicationStatus === APP_STATUS.SANCTIONED ||
                            applicationStatus === APP_STATUS.DISBURSED
                              ? "right-side-box-provision"
                              : "right-side-box"
                          }
                         style={isXsOrSm ? {width: "100%"} : {width: "100%",minHeight: "170px" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <img
                              src={
                                !partnerStartedJourney &&
                                normalizedProvisionalOffer === "yes"
                                  ? textFile
                                  : LockSquare
                              }
                              alt="lock-square"
                            />
                            <Typography className="right-side-heading">
                              {message}
                            </Typography>
                          </Box>

                          <Grid
                            container
                            spacing={2}
                            sx={{ textAlign: "left" }}
                          >
                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                {partnerStartedJourney
                                  ? "Loan Amount"
                                  : "Offer loan amount"}
                              </Typography>
                              <Typography className="right-side-value">
                                {formatCurrency(finalLoanAmountValue)}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                Tenure
                              </Typography>
                              <Typography className="right-side-value">
                                {finalMaxTenureValue}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                {partnerStartedJourney && isSanctioned
                                  ? "VAS Amount"
                                  : "Repayment type"}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography className="right-side-value" sx={{ textTransform: 'capitalize' }}>
                                  {partnerStartedJourney && isSanctioned
                                    ? formatCurrency(finalVasAmountValue)
                                    : (shouldShowValue && getRepaymentLabel(breData?.Repayment_Type)) || "-"
                                  }
                                </Typography>
                                
                                {!partnerStartedJourney && breData?.Repayment_Type && (
                                  <Tooltip 
                                    title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>} 
                                    arrow 
                                    placement="top"
                                    enterTouchDelay={0}
                                    leaveTouchDelay={3000} 
                                  >
                                    <InfoOutlined 
                                      sx={{ 
                                        fontSize: "16px", 
                                        cursor: "pointer", 
                                        color: "action.active",
                                        userSelect: "auto",
                                        WebkitUserSelect: "auto" 
                                      }} 
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                Currency
                              </Typography>
                              <Typography className="right-side-value">
                                {finalCurrencyValue}
                              </Typography>
                            </Grid>
                          </Grid>
                        </div>
                      ) : !partnerStartedJourney &&
                        normalizedProvisionalOffer === "no" ? (
                        <>
                          <div
                            className={
                              applicationStatus === APP_STATUS.SANCTIONED ||
                              applicationStatus === APP_STATUS.DISBURSED
                                ? "right-side-box-provision"
                                : "right-side-box"
                            }
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              ...(isXsOrSm ? {} : {minHeight: "170px" }),
                            }}
                          >
                            <Grid
                              container
                              spacing={2}
                              sx={{
                                textAlign: "center",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                px: '10px'
                              }}
                            >
                              <Typography
                                className="right-side-heading"
                                sx={{ mb: 0 }}
                              >
                                {message}
                              </Typography>
                            </Grid>
                          </div>
                        </>
                      ) : applicationStatus !== APP_STATUS.DISBURSED &&
                        applicationStatus !== APP_STATUS.ELIGIBILITY_FAILED &&
                        applicationStatus !== APP_STATUS.APPLICATION_REJECTED &&
                        applicationStatus !== APP_STATUS.APPLICATION_CLOSED &&
                        applicationStatus !== APP_STATUS.DECLINED_BY_CREDIT ? (
                        <div
                          className={
                            (!partnerStartedJourney &&
                              normalizedProvisionalOffer === "yes") ||
                            applicationStatus === APP_STATUS.SANCTIONED ||
                            applicationStatus === APP_STATUS.DISBURSED
                              ? "right-side-box-provision"
                              : "right-side-box"
                          }
                          style={isXsOrSm ? {width: "100%"} : {width: "100%",minHeight: "170px" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                            }}
                          >
                            <img
                              src={
                                !partnerStartedJourney &&
                                normalizedProvisionalOffer === "yes"
                                  ? textFile
                                  : LockSquare
                              }
                              alt="lock-square"
                            />
                            <Typography className="right-side-heading">
                              {message}
                            </Typography>
                          </Box>

                          <Grid
                            container
                            spacing={2}
                            sx={{ textAlign: "left" }}
                          >
                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                {partnerStartedJourney
                                  ? "Loan Amount"
                                  : "Offer loan amount"}
                              </Typography>
                              <Typography className="right-side-value">
                                {formatCurrency(finalLoanAmountValue)}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                Tenure
                              </Typography>
                              <Typography className="right-side-value">
                                {finalMaxTenureValue}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                {partnerStartedJourney && isSanctioned
                                  ? "VAS Amount"
                                  : "Repayment type"}
                              </Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                <Typography className="right-side-value" sx={{ textTransform: 'capitalize' }}>
                                  {partnerStartedJourney && isSanctioned
                                    ? formatCurrency(finalVasAmountValue)
                                    : (shouldShowValue && getRepaymentLabel(breData?.Repayment_Type)) || "-"
                                  }
                                </Typography>
                                
                                {!partnerStartedJourney && breData?.Repayment_Type && (
                                  <Tooltip 
                                    title={<Box sx={{ p: 0.5 }}>{activeTooltip}</Box>} 
                                    arrow 
                                    placement="top"
                                    enterTouchDelay={0}
                                    leaveTouchDelay={3000} 
                                  >
                                    <InfoOutlined 
                                      sx={{ 
                                        fontSize: "16px", 
                                        cursor: "pointer", 
                                        color: "action.active",
                                        userSelect: "auto",
                                        WebkitUserSelect: "auto" 
                                      }} 
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography className="right-side-lable">
                                Currency
                              </Typography>
                              <Typography className="right-side-value">
                                {finalCurrencyValue}
                              </Typography>
                            </Grid>
                          </Grid>
                        </div>
                      ) : (
                        <>
                          <div
                            className={
                              applicationStatus === APP_STATUS.SANCTIONED ||
                                applicationStatus === APP_STATUS.DISBURSED
                                ? "right-side-box-provision"
                                : "right-side-box"
                            }
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              ...(isXsOrSm ? {} : {minHeight: "170px" }),
                            }}
                          >
                            <Grid
                              container
                              spacing={2}
                              sx={{
                                textAlign: "center",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                px: '10px'
                              }}
                            >
                              <Typography
                                className="right-side-heading"
                                sx={{ mb: 0 }}
                              >
                                {message}
                              </Typography>
                            </Grid>
                          </div>
                        </>
                      )}
                    </Grid>
                  </Grid>
                )}
               
                {triggerData?.toUser !== false && (
                  <Box sx={{ mt: 2, mb: 2 }}>
                    <NextStepBanner status={fetchUserApplicationData?.status || ""} managerName={fetchUserApplicationData?.assignedSMName} />
                  </Box>
                )}
                {/* {isXsOrSm && !partnerStartedJourney && pageTypeSummary !== "loansummary" && (
                  <Box sx={{ display: "flex", justifyContent: "end" }}>
                    <PrimaryButtonSecond
                    onClick={(e: any) => {
                      if(handleContinue) {
                        handleContinue(e);
                      }
                    }}
                    disabled={isContinueLoading}
                    sx={{
                      width: "100%",
                      py: 1.5,
                      fontSize: "16px",
                      fontWeight: 600,
                      opacity: isContinueLoading ? 0.6 : 1,
                    }}
                >
                  {isContinueLoading ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1, color: "inherit" }} />
                        Loading...
                    </>
                  ) : (
                    "Continue"
                  )}
                </PrimaryButtonSecond>
                </Box>)} */}
              </div>
            )}
          </Box>
        )}

        {/* {!isXsOrSm && showApplicationDetailsCard && (
          <Typography className="section-heading">Upload Documents</Typography>
        )} */}
        {(activeLayoutTab === 0 || activeLayoutTab === 1) && (
        <Box mt={1} mb={1}>
          <Box ref={setDocumentsHeaderActionsPortalTarget} />
        </Box>
      )}
    
      
      <Box mt={1} mb={1}>
        <TabLayout
          tabs={tabList.map((t:any)=>t.label)}
          activeIndex={tabList.findIndex((t:any)=>t.id === activeLayoutTab)}
          onTabChange={(index) => {
            const selectedTab = tabList[index];
            if (selectedTab) {
              dispatch(setSelectedIndex(selectedTab.id));
            }
            dispatch(setSummaryApplicationEditActive(false));
          }}
        />
      </Box>

      <Box mt={2}>
        {handleViewTabSection}
      </Box>
      </>

      {/* <UploadDocument
        tabsList={tabsList}
        applicationDetail={applicationDetail}
        mainApplicantDetails={mainApplicantDetails}
        coApplicantDetails={coApplicantDetails}
        handleFetchApplicationDetail={handleFetchApplicationDetail}
        partnerStartedJourney={partnerStartedJourney}
        dropdownData={dropdownData}
        applicationStatus={applicationStatus}
        coApplicantEmploymentType={coApplicantEmploymentType}
        isPayPartyPresent={isPayPartyPresent}
        isPayPartyValue={isPayPartyValue}
      /> */}
    </>
  );
};

export default Summary;
